import React, { useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  RotateCcw,
  Check,
  Search,
  X,
  ChevronDown,
  Lock,
  LayoutGrid,
  DollarSign,
  Barcode,
  Boxes,
  SlidersHorizontal,
} from 'lucide-react';
import type { ProductFieldConfig } from '@/hooks/productos/useProductoFieldsConfig';
import { Drawer } from '@/contasis/components/Drawer/Drawer';
import { Button } from '@/contasis/components/Button/Button';
import { Switch } from '@/contasis/components/Switch/Switch';
import { Checkbox } from '@/contasis/components/Checkbox/Checkbox';
import { InlineAlert } from '@/contasis/components/InlineAlert/InlineAlert';
import { Dialog } from '@/contasis/components/Dialog/Dialog';

interface FieldCategoryMeta {
  id: ProductFieldConfig['category'];
  label: string;
  hint: string;
  icon: LucideIcon;
}

const FIELD_CATEGORIES: FieldCategoryMeta[] = [
  { id: 'basic', label: 'Básicos', hint: 'Categoría y disponibilidad del producto', icon: LayoutGrid },
  { id: 'pricing', label: 'Precios', hint: 'Costo, ganancia y descuentos', icon: DollarSign },
  { id: 'codes', label: 'Códigos', hint: 'Códigos de barras, SUNAT y fábrica', icon: Barcode },
  { id: 'inventory', label: 'Inventario', hint: 'Tipo de existencia y presentaciones', icon: Boxes },
  { id: 'advanced', label: 'Avanzado', hint: 'Marca, modelo, imagen y peso', icon: SlidersHorizontal },
];

interface FieldRowProps {
  field: ProductFieldConfig;
  onToggleVisible: (fieldId: string) => void;
  onToggleRequired: (fieldId: string) => void;
  categoryTag?: string;
}

function FieldRow({ field, onToggleVisible, onToggleRequired, categoryTag }: FieldRowProps) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-muted last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="text-ui-sm font-medium text-primary">{field.label}</div>
        {categoryTag && (
          <div className="text-label text-muted mt-0.5">{categoryTag}</div>
        )}
      </div>

      <div className="flex items-center gap-3.5 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-caption whitespace-nowrap ${field.visible ? 'text-muted' : 'text-disabled'}`}>
            Obligatorio
          </span>
          <Switch
            size="sm"
            checked={field.required}
            disabled={!field.visible}
            onChange={() => onToggleRequired(field.id)}
          />
        </div>

        <Switch
          size="sm"
          checked={field.visible}
          onChange={() => onToggleVisible(field.id)}
        />
      </div>
    </div>
  );
}

interface FieldCategoryGroupProps {
  category: FieldCategoryMeta;
  fields: ProductFieldConfig[];
  defaultOpen: boolean;
  onToggleVisible: (fieldId: string) => void;
  onToggleRequired: (fieldId: string) => void;
}

function FieldCategoryGroup({
  category,
  fields,
  defaultOpen,
  onToggleVisible,
  onToggleRequired,
}: FieldCategoryGroupProps) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = category.icon;
  const visibleCount = fields.filter(f => f.visible).length;

  return (
    <div className="mb-2.5 border border-muted rounded-[10px] overflow-hidden">
      <button
        type="button"
        className="w-full flex items-start gap-2.5 p-3 bg-surface-1 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span className="w-7 h-7 rounded-[7px] bg-brand-light text-brand flex items-center justify-center flex-shrink-0">
          <Icon size={15} aria-hidden />
        </span>

        <span className="flex-1 min-w-0">
          <span className="block text-ui-sm font-semibold text-primary">{category.label}</span>
          <span className="block text-caption text-muted">{category.hint}</span>
        </span>

        <span className="self-center text-caption-medium text-tertiary bg-surface-0 border border-muted rounded-full px-2 py-0.5 whitespace-nowrap">
          {visibleCount}/{fields.length}
        </span>

        <span className={`self-center text-muted flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}>
          <ChevronDown size={16} aria-hidden />
        </span>
      </button>

      {open && (
        <div className="px-3 pb-2 pt-1">
          {fields.map(field => (
            <FieldRow
              key={field.id}
              field={field}
              onToggleVisible={onToggleVisible}
              onToggleRequired={onToggleRequired}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface FieldsConfigPanelProps {
  isOpen: boolean;
  onClose: () => void;
  fieldsConfig: ProductFieldConfig[];
  onApply: (config: ProductFieldConfig[]) => void;
  onReset: () => void;
}

export const FieldsConfigPanel: React.FC<FieldsConfigPanelProps> = ({
  isOpen,
  onClose,
  fieldsConfig,
  onApply,
  onReset,
}) => {
  const [draft, setDraft] = useState<ProductFieldConfig[]>(fieldsConfig);
  const [searchQuery, setSearchQuery] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);

  // Mientras el panel está abierto, los cambios viven en este borrador local;
  // recién se guardan de verdad al presionar "Aplicar". Si el drawer se cierra
  // sin aplicar (X, click afuera, Escape), el borrador se descarta solo porque
  // nunca se usó fuera de este componente.
  useEffect(() => {
    if (isOpen) {
      setDraft(fieldsConfig);
      setSearchQuery('');
    }
  }, [isOpen, fieldsConfig]);

  const systemFields = useMemo(() => draft.filter(f => f.isSystemRequired), [draft]);
  const customizableFields = useMemo(() => draft.filter(f => !f.isSystemRequired), [draft]);

  const visibleCount = customizableFields.filter(f => f.visible).length;
  const requiredCount = customizableFields.filter(f => f.required).length;
  const totalCount = customizableFields.length;
  const allVisibleState: 'all' | 'some' | 'none' =
    visibleCount === 0 ? 'none' : visibleCount === totalCount ? 'all' : 'some';

  const toggleDraftVisibility = (fieldId: string) => {
    setDraft(prev => prev.map(field =>
      field.id === fieldId && !field.isSystemRequired
        ? { ...field, visible: !field.visible }
        : field
    ));
  };

  const toggleDraftRequired = (fieldId: string) => {
    setDraft(prev => prev.map(field =>
      field.id === fieldId && !field.isSystemRequired && field.visible
        ? { ...field, required: !field.required }
        : field
    ));
  };

  const setAllDraftVisible = (visible: boolean) => {
    setDraft(prev => prev.map(field =>
      field.isSystemRequired ? field : { ...field, visible }
    ));
  };

  const categoryLabelById = useMemo(
    () => Object.fromEntries(FIELD_CATEGORIES.map(c => [c.id, c.label])),
    []
  );

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return null;
    return customizableFields.filter(f => f.label.toLowerCase().includes(q));
  }, [searchQuery, customizableFields]);

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleConfirmReset = () => {
    onReset();
    setShowResetDialog(false);
  };

  let firstOpenAssigned = false;

  const footer = (
    <div className="flex items-center justify-between gap-3 w-full">
      <span className="text-caption text-muted">
        {visibleCount} campos visibles · {requiredCount} obligatorios
      </span>
      <div className="flex items-center gap-2.5">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          icon={<RotateCcw className="w-3.5 h-3.5" />}
          onClick={() => setShowResetDialog(true)}
        >
          Restablecer
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          icon={<Check className="w-3.5 h-3.5" />}
          onClick={handleApply}
        >
          Aplicar
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title="Configurar Campos"
        size="sm"
        className="!z-[1200]"
        overlayClassName="!z-[1150]"
        footer={footer}
      >
        <div className="relative mb-5">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle pointer-events-none"
            aria-hidden
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar un campo, ej. Precio de compra"
            className="w-full text-ui-sm font-medium text-primary bg-surface-1 border border-default rounded-lg pl-9 pr-9 py-2 focus:bg-surface-0 focus:border-brand outline-none"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:bg-surface-hover rounded-md p-1"
              aria-label="Borrar búsqueda"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div>
          <h6 className="text-caption-medium uppercase tracking-wide text-secondary mb-2.5">
            Campos del sistema
          </h6>
          <InlineAlert variant="warning" size="sm">
            Estos campos son necesarios y no se pueden deshabilitar
          </InlineAlert>
          <div className="flex flex-wrap gap-2 mt-2.5">
            {systemFields.map(field => (
              <span
                key={field.id}
                className="inline-flex items-center gap-1.5 bg-surface-1 border border-muted text-tertiary text-caption font-medium pl-2 pr-2.5 py-1.5 rounded-full"
              >
                <Lock size={12} className="text-subtle" aria-hidden />
                {field.label}
              </span>
            ))}
          </div>
        </div>

        <div className="border-t border-muted my-5" />

        <div>
          <h6 className="text-caption-medium uppercase tracking-wide text-secondary mb-2.5">
            Campos personalizables
          </h6>

          {searchResults ? (
            <div>
              <p className="text-caption text-muted mb-2">
                {searchResults.length} campo{searchResults.length === 1 ? '' : 's'} encontrado
                {searchResults.length === 1 ? '' : 's'}
              </p>
              {searchResults.length === 0 ? (
                <p className="text-body-sm text-muted text-center py-8">
                  No encontramos un campo llamado "{searchQuery}". Prueba con otra palabra.
                </p>
              ) : (
                searchResults.map(field => (
                  <FieldRow
                    key={field.id}
                    field={field}
                    onToggleVisible={toggleDraftVisibility}
                    onToggleRequired={toggleDraftRequired}
                    categoryTag={categoryLabelById[field.category]}
                  />
                ))
              )}
            </div>
          ) : (
            <>
              <InlineAlert variant="info" size="sm" className="mb-3">
                Activa los campos que necesites y márcalos como obligatorios según tu negocio
              </InlineAlert>

              <label className="flex items-center gap-2.5 bg-surface-1 border border-muted rounded-lg px-3 py-2.5 mb-3.5 cursor-pointer">
                <Checkbox
                  checked={allVisibleState === 'all'}
                  indeterminate={allVisibleState === 'some'}
                  onChange={(e) => setAllDraftVisible(e.target.checked)}
                  size="sm"
                />
                <span className="text-ui-sm font-medium text-secondary flex-1">
                  Activar todos
                </span>
                <span className="text-caption text-muted">
                  {visibleCount} de {totalCount} visibles
                </span>
              </label>

              {FIELD_CATEGORIES.map(category => {
                const fieldsInCategory = customizableFields.filter(f => f.category === category.id);
                if (fieldsInCategory.length === 0) return null;
                const defaultOpen = !firstOpenAssigned;
                firstOpenAssigned = true;
                return (
                  <FieldCategoryGroup
                    key={category.id}
                    category={category}
                    fields={fieldsInCategory}
                    defaultOpen={defaultOpen}
                    onToggleVisible={toggleDraftVisibility}
                    onToggleRequired={toggleDraftRequired}
                  />
                );
              })}
            </>
          )}
        </div>
      </Drawer>

      {showResetDialog && (
        <Dialog
          type="warning"
          title="Restablecer configuración"
          message="¿Estás seguro de restablecer la configuración a los valores predeterminados?"
          onClose={() => setShowResetDialog(false)}
          onConfirm={handleConfirmReset}
          confirmText="Restablecer"
          cancelText="Cancelar"
        />
      )}
    </>
  );
};


